import { supabase } from '@/integrations/supabase/client';

export interface SearchResult {
  id: string;
  platform: string;
  profileName: string;
  profileUrl: string;
  profileBio: string;
  profileImageUrl: string;
  confidenceScore: number;
  lastUpdated: string;
}

const mockResults: Record<string, SearchResult[]> = {
  email: [
    {
      id: '1',
      platform: 'LinkedIn',
      profileName: 'John Profile',
      profileUrl: 'https://linkedin.com',
      profileBio: 'Professional background',
      profileImageUrl: 'https://api.dicebear.com/7.x/avataaars/svg?seed=john',
      confidenceScore: 0.92,
      lastUpdated: new Date().toISOString(),
    },
    {
      id: '2',
      platform: 'Twitter',
      profileName: '@johnhandle',
      profileUrl: 'https://twitter.com',
      profileBio: 'Tech & life tweets',
      profileImageUrl: 'https://api.dicebear.com/7.x/avataaars/svg?seed=twitter',
      confidenceScore: 0.85,
      lastUpdated: new Date().toISOString(),
    },
  ],
  username: [
    {
      id: '3',
      platform: 'Instagram',
      profileName: 'username_profile',
      profileUrl: 'https://instagram.com',
      profileBio: 'Instagram presence',
      profileImageUrl: 'https://api.dicebear.com/7.x/avataaars/svg?seed=insta',
      confidenceScore: 0.88,
      lastUpdated: new Date().toISOString(),
    },
  ],
  mobile: [
    {
      id: '4',
      platform: 'WhatsApp',
      profileName: 'Contact Name',
      profileUrl: 'https://wa.me',
      profileBio: 'Available on WhatsApp',
      profileImageUrl: 'https://api.dicebear.com/7.x/avataaars/svg?seed=whatsapp',
      confidenceScore: 0.90,
      lastUpdated: new Date().toISOString(),
    },
  ],
};

export async function performSearchLocal(
  searchType: 'email' | 'username' | 'mobile',
  query: string
) {
  await new Promise(resolve => setTimeout(resolve, 500));
  const results = mockResults[searchType] || [];
  return { results, count: results.length };
}

export async function performSearch(
  searchType: 'email' | 'username' | 'mobile',
  query: string,
  userId: string
) {
  try {
    // Save search to database
    const { data: searchRecord, error: searchError } = await supabase
      .from('searches')
      .insert({
        user_id: userId,
        search_type: searchType,
        query,
      })
      .select()
      .single();

    if (searchError) throw searchError;

    // Currently no results - ready for real API integration
    const results: SearchResult[] = [];

    // Save results to database
    if (results.length > 0) {
      const resultsToInsert = results.map((result) => ({
        search_id: searchRecord.id,
        platform: result.platform,
        profile_name: result.profileName,
        profile_url: result.profileUrl,
        profile_bio: result.profileBio,
        profile_image_url: result.profileImageUrl,
        confidence_score: result.confidenceScore,
        data: JSON.stringify(result),
      }));

      await supabase.from('search_results').insert(resultsToInsert);
    }

    return {
      searchId: searchRecord.id,
      results: results,
      count: results.length,
    };
  } catch (error) {
    console.error('Search error:', error);
    throw error;
  }
}

export async function addToFavorites(
  userId: string,
  resultId: string
) {
  const { error } = await supabase
    .from('favorites')
    .insert({
      user_id: userId,
      result_id: resultId,
    });

  if (error) throw error;
}

export async function removeFromFavorites(
  userId: string,
  resultId: string
) {
  const { error } = await supabase
    .from('favorites')
    .delete()
    .eq('user_id', userId)
    .eq('result_id', resultId);

  if (error) throw error;
}

export async function getUserFavorites(userId: string) {
  const { data, error } = await supabase
    .from('favorites')
    .select(
      `
      result_id,
      search_results (*)
    `
    )
    .eq('user_id', userId);

  if (error) throw error;
  return data;
}

export async function getUserSearchHistory(userId: string) {
  const { data, error } = await supabase
    .from('searches')
    .select(
      `
      id,
      search_type,
      query,
      created_at,
      search_results (*)
    `
    )
    .eq('user_id', userId)
    .order('created_at', { ascending: false })
    .limit(20);

  if (error) throw error;
  return data;
}